import { Component, ViewChild, ElementRef, Injector } from '@angular/core';
import { ModalController, PopoverController, IonicModule } from '@ionic/angular';
import type { OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
// import type { ModalOptions, PopoverOptions } from '@ionic/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { BasePage } from '@shared/common';
import {
    AuthService, ConnectivityService, DateService, EventsService, LocalDataService,
    SessionService, SettingsService, SmsService, UtilsService, UxHelperService,
    VariableReplacementService, IoniconsService, FirestorePathsService, FirebaseFunctionsService
} from '@shared/services';

import { CalendarModalPage } from '../calendarModal/calendarModal.page';
import { PopoverMenuPage } from '../popoverMenu/popoverMenu.page';
import { Constants } from '@shared/models';
import type { TPopoverMenuItem, TResponse, TSetting, TTimestamp } from '@shared/models';

@Component({
    'selector': 'app-viewBase',
    'templateUrl': './viewBase.page.html',
    'styleUrls': ['./viewBase.page.scss'],
    'standalone': true,
    'imports': [IonicModule, CommonModule, FormsModule]
})
export class ViewBasePage extends BasePage implements OnInit, OnDestroy {
    @ViewChild('header', { read: ElementRef }) private headerRef!: ElementRef;
    @ViewChild('headerToolbar', { read: ElementRef }) private headerToolbarRef!: ElementRef;
    @ViewChild('title', { read: ElementRef }) private titleRef!: ElementRef;
    private currentTitle = '';
    private settingsSubscription: any;
    // private subscriptions: any = [];
    // private isHelpVisible = false;  // this must match the default opacity setting in variables.scss
    protected auth: AuthService;
    // protected utilsService: UtilsService;
    protected eventsService: EventsService;
    protected smsService!: SmsService;
    // protected override connectivityService: ConnectivityService;
    protected localDataService: LocalDataService;
    // protected uxHelperService: UxHelperService;
    protected sessionService: SessionService;
    protected settingsService: SettingsService;
    protected variableReplacementService: VariableReplacementService;
    protected dateService: DateService;
    protected Constants = Constants;
    protected modalController: ModalController;
    protected popoverController: PopoverController;
    protected ioniconsService: IoniconsService;
    protected firebaseFunctionsService: FirebaseFunctionsService;
    protected router: Router;
    // private pathsService: FirestorePathsService;
    // protected window: Window;

    protected helpDocName!: string;
    protected pageName!: string;
    private setting!: TSetting;
    private popoverMenuItems: any[] = [];
    private moreMenuItemClickCallback: any;
    public vb: any = {};

    constructor(protected override injector: Injector) {
        super(injector);
        this.popoverController = injector.get(PopoverController);
        this.auth = injector.get(AuthService);
        // this.utilsService = injector.get(UtilsService);
        this.eventsService = injector.get(EventsService);
        // this.uxHelperService = injector.get(UxHelperService);
        this.modalController = injector.get(ModalController);
        this.localDataService = injector.get(LocalDataService);
        // this.connectivityService = injector.get(ConnectivityService);
        this.sessionService = injector.get(SessionService);
        this.settingsService = injector.get(SettingsService);
        this.variableReplacementService = injector.get(VariableReplacementService);
        this.dateService = injector.get(DateService);
        this.ioniconsService = injector.get(IoniconsService);    // Needed for Ionic v7
        this.firebaseFunctionsService = injector.get(FirebaseFunctionsService);
        this.router = injector.get(Router);
        this.smsService = this.injector.get(SmsService);

        // this.pathsService = injector.get(FirestorePathsService);
        // this.window = injector.get(Window);
    }

    // #void-instead-of-await-in-constructor-or-ngOnInit
    public override ngOnInit(): void {
        // Somebody is asking the ViewBasePage for settings.  However, the Welcome view and the Login
        // view are both derived from ViewBase.  Neither class requires settings.  Settings is account
        // dependent and only has a valid value after login.
        // this.getSettings();

        // When the internet connection regionName changes then re/enable the login button.
        this.vb.isOnline = this.connectivityService.isOnline();
        this.vb.disabled = !this.vb.isOnline;


        this.eventsService.subscribe(Constants.EVENT_INTERNET_CONNECTION_CHANGE, (data: any) => {
            this.onInternetConnectionStateChangeEvent(data);
        });

        // this.connectivityService.monitor().subscribe((isConnected) => {
        //     this.vb.isOnline = isConnected;     // this.connectivityService.isOnline();
        //     this.vb.disabled = !this.vb.isOnline;
        //     const title = (this.vb.isOnline) ? this.currentTitle : 'No Internet Connection';
        //     const kolor = (this.vb.isOnline) ? '#3d4e66' : '#cccccc';

        //     if (this.titleRef) {
        //         this.titleRef.nativeElement.innerHTML = title;
        //         document.body.style.setProperty('--myDisabledEnabledColor', kolor);
        //     }
        // });
    }

    public ngOnDestroy(): void {
        if (this.settingsSubscription) {
            this.settingsSubscription.unsubscribe();
        }
        // this.subscriptions.forEach((subscription: any) => {
        //     if (subscription) {
        //         subscription.unsubscribe();
        //     }
        // });
    }

    protected ionViewDidEnter(): void {
        if (this.titleRef) {
            this.currentTitle = this.titleRef.nativeElement.innerHTML;
        }
        // Try to find the Usetiful window so I can close it.
        // for (let i = 0; i < window.frames.length; i++) {
        //     console.log(window.frames[i]);
        // }
    }

    // When the internet connection regionName changes then re/enable the login button.
    protected onInternetConnectionStateChangeEvent(isConnected: boolean): void {
        try {
            this.vb.isOnline = isConnected;
            this.vb.disabled = !this.vb.isOnline;
            const title = (this.vb.isOnline) ? this.currentTitle : 'No Internet Connection';
            const kolor = (this.vb.isOnline) ? '#3d4e66' : '#cccccc';

            if (this.titleRef) {
                this.titleRef.nativeElement.innerHTML = title;
                document.body.style.setProperty('--myDisabledEnabledColor', kolor);
            }
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            this.uxHelperService.showAlert('Warning', errorMessage);
            // return null;
        }
    }

    // The Welcome view and the Login view are both derived from ViewBase.  However,
    // neither class requires settings.  Settings is account dependent and only has a
    // valid value after login.
    // private async getSettings(): Promise<void> {
    //     const accountNumberPath = this.pathsService.getAccountNumberPath();
    //     if (!accountNumberPath || accountNumberPath.length === 0) {
    //         return;
    //     }
    //     const settingsObservable = this.settingsService.getObservable();
    //     if (settingsObservable) {
    //         this.settingsSubscription = settingsObservable.subscribe(async (settings: TSetting[]) => {
    //             if (settings && settings.length > 0) {
    //                 this.setting = settings[0];
    //                 // await this.variableReplacementService.initialize(this.setting);
    //                 if (!this.smsService) {
    //                     this.smsService = this.injector.get(SmsService);
    //                 }
    //                 this.smsService.initialize(this.setting);
    //             }
    //         });
    //         // this.registerSubscription(settingsSubscription);
    //     }
    // }

    // protected registerSubscription(subscription) {
    //     if (subscription) {
    //         this.subscriptions.push(subscription);
    //     }
    // }

    // The Welcome view and the Login view are both derived from ViewBase.  However,
    // neither class requires settings.  Settings is account dependent and only has a
    // valid value after login.
    // private async getSettings(): Promise<void> {
    //     const settingsObservable = this.settingsService.getObservable();
    //     if (settingsObservable) {
    //         this.settingsSubscription = settingsObservable.subscribe(async (settings: TSetting[]) => {
    //             if (settings && settings.length > 0) {
    //                 this.setting = settings[0];
    //                 // await this.variableReplacementService.initialize(this.setting);
    //                 if (!this.smsService) {
    //                     this.smsService = this.injector.get(SmsService);
    //                 }
    //                 this.smsService.initialize(this.setting);
    //             }
    //         });
    //         // this.registerSubscription(settingsSubscription);
    //     }
    // }

    protected setCurrentView(viewName: string): void {
        if (viewName) {
            this.pageName = viewName;
            // The EVENT_PAGE_LOADED will be caught by app.component.ts file.
            // In response to this event, it will highlight the proper menuitem.
            // const viewNameProper = this.utilsService.toTitleCase(viewName);
            // this.eventsService.publish(Constants.EVENT_PAGE_LOADED, viewNameProper);
            this.eventsService.publish(Constants.EVENT_PAGE_LOADED, viewName);
        }
    }

    protected internetConnected(): boolean {
        return this.connectivityService.isOnline();
    }

    protected isOnline(): boolean {
        return this.connectivityService.isOnline();
    }

    // Angular doesn't have a way to pass complex objects on the router.
    // So I will use this SessionService as a way to hold the objects that I need to pass around.
    protected setViewModel(vm: any): void {
        this.sessionService.setViewModel(vm);
    }
    public getViewModel(): any {
        return this.sessionService.getViewModel();
    }

    // public async onExecuteAutoTypeClick(event: any, formName: string) {
    //     // let formData: any = null;
    //     const data = await this.localDataService.readFile(this.Constants.FILENAME_TUTORIAL_DATA);
    //     const formData = data[formName];
    //     if (formData.length > 0) {
    //         // const uxHelper = this.injector.get(UxHelperService);
    //         this.typeFormDataIntoFields(formData, this);
    //     }
    // }

    // private typeFormDataIntoFields(formData: any, self: any) {
    //     (async function loop() {
    //         for (let idx = 0; idx < formData.length; idx++) {
    //             const fieldData = formData[idx];

    //             const element = self.getElement(fieldData.fieldName);
    //             if (element) {
    //                 await self.typeIntoInputField(element, fieldData.value)
    //                 .then(async () => {
    //                     // Wait a moment before typing into the next field.
    //                     await self.sleep(300);
    //                 });
    //             }
    //         }
    //     })();
    // }

    // protected typeIntoInputField(element: any, text: string) {
    //     const promise = new Promise(async (resolve, reject) => {

    //         // Change the font color, set the input focus and initialize the field to empty.
    //         const elementRef = element.elementRef;
    //         elementRef._elementRef.nativeElement.style.color = '#ff0000';
    //         elementRef.setFocus();
    //         elementRef.value = '';

    //         //
    //         this.uxHelperService.playTypingSound();
    //         await this.sleep(300);

    //         for (let idx = 0; idx < text.length; idx++) {
    //             const ch = text.substring(idx, idx + 1);
    //             elementRef.value = elementRef.value + ch;
    //             await this.sleep(150);  // wait 150ms between keystrokes.  It looks better
    //         }
    //         this.uxHelperService.stopSound();
    //         resolve(0);
    //     });
    //     return promise;
    // }

    // protected async helpModalShow() {
    //     const options: ModalOptions = {
    //         'component': HelpModalPage,
    //         // componentProps: { 'pageName': this.pageName, 'helpDocName': this.helpDocName, 'domain': this.domain },
    //         // componentProps: { 'pageName': this.pageName, 'domain': this.domain },
    //         'componentProps': { 'pageName': this.pageName, 'domain': this.sessionService.getDomain() },
    //         'backdropDismiss': true,
    //         'showBackdrop': true,
    //         'cssClass': 'myModalSize'
    //     };

    //     const myModal = await this.modalController.create(options);
    //     myModal.present();
    // }

    // public hideHelp(): void {
    //     anime.set('.helpContainer', { 'opacity': '0' });
    //     this.isHelpVisible = false;
    // }

    // private animeToggleHelpVisibility() {
    //     this.isHelpVisible = !this.isHelpVisible;

    //     const params = {
    //         targets: '.helpContainer',
    //         opacity: '0',
    //         easing: 'linear',
    //         // visibilitys: 'hidden',
    //         duration: '400'
    //     };
    //     // params.visibility = (this.isHelpVisible) ? 'visible' : 'hidden';
    //     params.opacity = (this.isHelpVisible) ? '1' : '0';
    //     anime(params);
    // }

    // public onPress() {
    //     this.helpModalShow();
    // }

    public onTitleClick(): void {
        // this.helpModalShow();
    }

    // public onToggleHelpClick(/* init?: boolean */) {
    //     // this.animeToggleHelpVisibility();
    //     this.helpModalShow();
    // }

    protected async openCalendarModal(dateRangeStart: TTimestamp, dateRangeEnd: TTimestamp,
        selectedDate: TTimestamp, title: string): Promise<TResponse> {
        const options: any = {
            // 'component': CalendarModalPage,
            // 'component': CalendarModalPageComponentModule,
            'component': CalendarModalPage,
            'componentProps': {
                'title': title,
                'dateRangeStart': dateRangeStart,
                'dateRangeEnd': dateRangeEnd,
                'selectedDate': selectedDate
            },
            'backdropDismiss': false,
            'showBackdrop': true,
            'cssClass': 'myCalendarModal'
        };

        const myModal = await this.modalController.create(options);
        await myModal.present();
        const response = await myModal.onDidDismiss() as TResponse;
        return response;
        // if (response.okay) {
        //     const selectedDate = response.selectedItems[0];
        //     const selectedTimestamp = this.dateService.parse(selectedDate, 'iso');

        // }
    }

    /*  Original version
    protected async openCalendarModal(dateRangeStart: TTimestamp, dateRangeEnd: TTimestamp,
        selectedDate: TTimestamp, title: string): Promise<TTimestamp> {
        // eslint-disable-next-line no-async-promise-executor
        const promise = new Promise(async (resolve) => {
            const options: any = {
                // 'component': CalendarModalPage,
                // 'component': CalendarModalPageComponentModule,
                'component': CalendarModalPage,
                'componentProps': {
                    'title': title,
                    'dateRangeStart': dateRangeStart,
                    'dateRangeEnd': dateRangeEnd,
                    'selectedDate': selectedDate
                },
                'backdropDismiss': false,
                'showBackdrop': true,
                'cssClass': 'myCalendarModal'
            };

            const myModal = await this.modalController.create(options);
            await myModal.onDidDismiss()
                .then((response: any) => {
                    if (response.data.okay) {
                        resolve(response.data.selectedDate);
                        // if (promptForStartDate) {
                        //     this.startingDate = response.data.selectedDate;
                        // } else {
                        //     this.endingDate = response.data.selectedDate;
                        // }
                        // this.verifyDateRange();
                    }
                });

            await myModal.present();
        });
        return promise as Promise<TTimestamp>;
    }

    */
    public resetPopoverMenu(): void {
        this.popoverMenuItems = [];
    }

    public pushPopoverMenuItem(menuItemName: string, testId = '', content = ''): void {
        // Prevent the Usetiful menu item from showing.
        if (menuItemName === 'Show Tour') {
            return;
        }
        const item: TPopoverMenuItem = { 'name': menuItemName, 'testId': testId, 'content': content };
        this.popoverMenuItems.push(item);
    }

    protected moreMenuItemClick(callback: any): void {
        this.moreMenuItemClickCallback = callback;
    }

    // , callback: any
    public async onMoreClick(event: Event): Promise<void> {
        const options: any = {
            // 'component': PopoverMenu,
            'component': PopoverMenuPage,
            'componentProps': { 'menuItems': this.popoverMenuItems },
            'event': event,
            'showBackdrop': false,
            'cssClass': 'myPopoverMenuClass',
        };

        const popover = await this.popoverController.create(options);
        await popover.present();
        const promise = popover.onDidDismiss()
            .then((response) => {
                if (response.data) {
                    if (this.moreMenuItemClickCallback) {
                        // Allow the derived page to handle the click.
                        // In the case of 'Show Tour', no special handling is necessary to show the usetiful tour.
                        this.moreMenuItemClickCallback(response.data.selectedItem, event);
                    }
                }
            });
        return promise;
    }
}
